import React, {useEffect, useRef, useState} from 'react';
import {Button, Col, DatePicker, Form, Input, Radio, Row, Select, Checkbox} from 'antd';
import {useParams} from 'react-router-dom';
import {useNavigate} from 'react-router-dom';
import {
  EventOptionTypes,
  AttendanceType,
  AttendanceTypeLabels,
  SponsorshipType,
  disablePrevDatesHandler,
  NumberWithoutDecimalValidation,
  NoEmptySpaceInput,
  validateEventOptionNumberInput,
  validateNumberInput,
} from '../../../../Util/commonUtility';
import dayjs from 'dayjs';
import {Option} from 'antd/es/mentions';
import {useDispatch} from 'react-redux';
import EventOptionDetails from './../Details/index';
import CommonEditor from '../../../CommonComponents/TextEditor';
import useLocalization from '../../../../Localization';
import {InfoCircleOutlined} from '@ant-design/icons';

const EventOptionsFormComponent = (props) => {
  const {ApplicationString} = useLocalization();
  const {id, eventId} = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitLabel, setSubmitLabel] = useState(
    ApplicationString['common.button.submitForm'],
  );
  const [form] = Form.useForm();
  const [showSponsorPerks, setShowSponsorPerks] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [vendor, setVendor] = useState();
  let navigate = useNavigate();
  let dispatch = useDispatch();
  const {
    postEventOptions,
    EditData,
    updateEventOptions,
    fetchEventOptionsById,
    eventsList,
    currentEvent,
    eventOptionGroupsList,
    fetching,
    setFetching,
    setEventOptionGroupsList,
  } = props;
  const [editorInfoState, setEditorInfoState] = useState(`<p></p>`);

  useEffect(() => {
    if (id) {
      fetchEventOptionsById(id);
      setSubmitLabel(ApplicationString['common.button.updateForm']);
    }
  }, []);
  useEffect(() => {
    if (id == EditData?.id) {
      if (EditData) {
        if (EditData?.event_option_type === EventOptionTypes.REGISTRATION) {
          setShowRegistration(true);
        }
        if (EditData?.event_option_type === EventOptionTypes.SPONSORSHIP) {
          setShowSponsorPerks(true);
        }
        setVendor(EditData?.is_vendor || false);
        setEditorInfoState(EditData?.eventSponsorshipPerk?.benefits);
        form.setFieldsValue({
          ...EditData,
          start_date: EditData?.start_date ? dayjs(EditData?.start_date) : null,
          is_vendor: EditData?.is_vendor,
          end_date: EditData?.end_date ? dayjs(EditData?.end_date) : null,
          table_space: EditData?.eventSponsorshipPerk?.table_space,
          raffle: EditData?.eventSponsorshipPerk?.raffle,
          free_lunch: EditData?.eventSponsorshipPerk?.free_lunch,
          workshop_attendees:
            EditData?.eventSponsorshipPerk?.workshop_attendees,
          attendees_price: EditData?.eventSponsorshipPerk?.attendees_price,
          attendees_note: EditData?.eventSponsorshipPerk?.attendees_note,
          slide_deck: EditData?.eventSponsorshipPerk?.slide_deck,
          presentation_note: EditData?.eventSponsorshipPerk?.presentation_note,
          flyer_distribution:
            EditData?.eventSponsorshipPerk?.flyer_distribution,
          dinner_with_speaker:
            EditData?.eventSponsorshipPerk?.dinner_with_speaker,
          event_option_group_id: {
            ...EditData.event_option_group_id,
            label: EditData?.event_option_group_id?.name,
            value: EditData?.event_option_group_id?.id,
          },
          benefits: EditData?.eventSponsorshipPerk?.benefits,
          sponsor_perk_content:
            EditData?.eventSponsorshipPerk?.sponsor_perk_content,
          sponsor_contact_content:
            EditData?.eventSponsorshipPerk?.sponsor_contact_content,
          lunch_price: EditData?.eventSponsorshipPerk?.lunch_price,
          dinner_with_speaker_price:
            EditData?.eventSponsorshipPerk?.dinner_with_speaker_price,
          vendor_rep_limit:
            EditData?.eventSponsorshipPerk?.vendor_rep_limit || 0,
          requires_qualification_form: EditData?.requires_qualification_form ?? false,
          tally_form_url: EditData?.tally_form_url ?? '',
        });
      }
    }
  }, [EditData]);

  // Attendance type (in-person / webcast / both) is only meaningful for a hybrid
  // event: one that is not fully online (is_online === false) but is available for
  // webcast (is_available_for_webcast === true). For any other event the selector is
  // hidden and the create/update payload defaults attendance_type to BOTH.
  // `currentEvent` is fetched directly by id in the container, so there is no need
  // to match it against a list of all events here.
  const isHybridEvent =
    !!currentEvent &&
    currentEvent?.is_online === false &&
    currentEvent?.is_available_for_webcast === true;

  const handleChange = (values) => {
    if (values === 'sponsorship') {
      setShowSponsorPerks(true);
      setShowRegistration(false);
    } else if (values === 'registration') {
      setShowRegistration(true);
      setShowSponsorPerks(false);
    } else {
      setShowRegistration(false);
      setShowSponsorPerks(false);
    }
  };

  const onFinish = async (values) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    const Data = {
      name: values?.name,
      currency_type: values?.currency_type,
      details: values.details,
      event_id: eventId ? Number(eventId) : Number(EditData?.event_id),
      event_option_group_id:
        values?.event_option_group_id?.value || values?.event_option_group_id,
      start_date: values.start_date,
      end_date: values.end_date,
      is_vendor: values?.is_vendor ? true : false,
      event_option_type: values.event_option_type,
      attendance_type: isHybridEvent
        ? values?.attendance_type || AttendanceType.BOTH
        : AttendanceType.BOTH,
      limit: values?.limit ? parseInt(values?.limit) : 0,
      qty: values?.qty ? parseInt(values?.qty) : 0,
      price: values?.price ? parseInt(values?.price) : 0,
      orders_count: values?.orders_count ? parseInt(values?.orders_count) : 0,
      sponsorship_type: values?.sponsorship_type,
      requires_qualification_form:
        showRegistration && values?.requires_qualification_form ? true : false,
      tally_form_url:
        showRegistration && values?.requires_qualification_form
          ? values?.tally_form_url?.trim()
          : null,
      sponsorshipPerk: {
        // table_space: values.table_space ? values.table_space : '',
        // raffle: values.raffle ? values.raffle : '',
        free_lunch: values.free_lunch ? parseInt(values.free_lunch) : 0,
        workshop_attendees: values.workshop_attendees
          ? parseInt(values.workshop_attendees)
          : 0,
        attendees_price: values.attendees_price
          ? parseInt(values.attendees_price)
          : 0,
        benefits: values.benefits,
        // attendees_note: values.attendees_note ? values.attendees_note : '',
        // slide_deck: values.slide_deck ? values.slide_deck : '',
        // presentation_note: values.presentation_note
        //   ? values.presentation_note
        //   : '',
        // flyer_distribution: values.flyer_distribution
        //   ? values.flyer_distribution
        //   : '',

        sponsor_perk_content: values?.sponsor_perk_content
          ? values.sponsor_perk_content
          : '',
        sponsor_contact_content: values?.sponsor_contact_content
          ? values?.sponsor_contact_content
          : '',
        lunch_price: values?.lunch_price ? parseInt(values.lunch_price) : 0,
        dinner_with_speaker_price: values?.dinner_with_speaker_price
          ? parseInt(values.dinner_with_speaker_price)
          : 0,
        dinner_with_speaker: values?.dinner_with_speaker
          ? parseInt(values.dinner_with_speaker)
          : 0,
        vendor_rep_limit: values?.vendor_rep_limit
          ? parseInt(values.vendor_rep_limit)
          : 0,
      },
    };

    try {
      if (id) {
        await updateEventOptions(Data, EditData.id);
      } else {
        await postEventOptions(Data);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };
  const onFinishFailed = (errorInfo) => {
    console.error(errorInfo);
  };
  const onReset = () => {
    if (isSubmitting) return;
    form.resetFields();
    setShowSponsorPerks(false);
  };
  const backToEventOptions = () => {
    if (eventId) {
      navigate(`/admin/eventOptions/${eventId}`);
    } else {
      navigate(`/admin/eventOptions/${EditData?.event_id}`);
    }
  };

  return (
    <div className='head-main-container'>
      <div className='main-form-container option-details'>
        <div className='search-container' style={{gap: '10px'}}>
          <Button onClick={() => navigate('/admin/events')} id='back-to-events'>
            {ApplicationString['common.button.backToEvents']}
          </Button>
        </div>
        <Row gutter={40} className='row-main-container'>
          <Col span={16}>
            <Form
              form={form}
              name='basic'
              onFinish={onFinish}
              layout='vertical'
              className='two-column-form'
              initialValues={''}
              onFinishFailed={onFinishFailed}
              autoComplete='off'>
              <div className='input-container'>
                <div className='form-column'>
                  <Form.Item
                    label='Event Option Type'
                    name='event_option_type'
                    rules={[
                      {
                        required: true,
                        message: 'Event Option Type should not be empty',
                      },
                    ]}>
                    <Select
                      showSearch
                      prefixCls='common-select-register'
                      className='space'
                      allowClear
                      onChange={handleChange}
                      optionFilterProp='children'
                      filterOption={(input, option) =>
                        option?.label
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      placeholder={'Select event option type'}
                      options={Object.values(EventOptionTypes).map((type) => ({
                        label: type.toUpperCase(),
                        value: type,
                      }))}
                    />
                  </Form.Item>
                </div>
              </div>
              <div className='input-container'>
                <div className='form-column'>
                  <Form.Item
                    label='Name'
                    name='name'
                    rules={[
                      {
                        required: true,
                        message: 'Event option name should not be empty',
                      },
                      {
                        validator: (_, value) =>
                          NoEmptySpaceInput(value, 'event option name'),
                      },
                    ]}>
                    <Input
                      prefixCls='common-input-user'
                      className='genral-form'
                      placeholder='Enter event option name'
                    />
                  </Form.Item>
                  <Form.Item
                    label='Price'
                    name='price'
                    rules={[
                      {required: true, message: 'Price should not be empty'},
                      {
                        validator: (_, value) =>
                          NumberWithoutDecimalValidation(
                            value,
                            'Price',
                            9999999,
                          ),
                      },
                    ]}>
                    <Input
                      prefixCls='common-input-user'
                      className='genral-form'
                      Number
                      min={0}
                      controls={false}
                      placeholder='Enter event option price'
                    />
                  </Form.Item>
                </div>
                <div className='form-column'>
                  <Form.Item
                    label='Quantity'
                    name='qty'
                    rules={[
                      {
                        required: true,
                        message: 'Quantity should not be empty',
                      },
                      {
                        validator: (_, value) =>
                          NumberWithoutDecimalValidation(value, 'Quantity'),
                      },
                    ]}>
                    <Input
                      prefixCls='common-input-user'
                      className='genral-form'
                      Number
                      min={0}
                      controls={false}
                      placeholder='Enter event option quantity'
                    />
                  </Form.Item>
                </div>
              </div>
              <div className='input-container'>
                <div className='form-column'>
                  <Form.Item
                    label={
                      <div className='detailLabelAndTextMessageContainer'>
                        <span className='detailsLabel'>Limit</span>
                        <span className='detailsTextMessage'>
                          <InfoCircleOutlined /> If you add limit as 0, it will
                          be treated as unlimited.
                        </span>
                      </div>
                    }
                    name='limit'
                    rules={[
                      {
                        validator: (_, value) =>
                          NumberWithoutDecimalValidation(value, 'Limit'),
                      },
                    ]}>
                    <Input
                      prefixCls='common-input-user'
                      className='genral-form'
                      placeholder='Set limit for event option'
                    />
                  </Form.Item>
                </div>
              </div>

              {showRegistration && isHybridEvent && (
                <div className='input-container'>
                  <div className='form-column'>
                    <Form.Item
                      label='Attendance Type'
                      name='attendance_type'
                      initialValue={AttendanceType.BOTH}
                      rules={[
                        {
                          required: true,
                          message: 'Attendance type should not be empty',
                        },
                      ]}>
                      <Radio.Group>
                        {Object.values(AttendanceType).map((type) => (
                          <Radio key={type} value={type}>
                            {AttendanceTypeLabels[type]}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                    <div
                      className='attendance-type-note'
                      style={{
                        marginTop: -12,
                        marginBottom: 8,
                        fontSize: 12,
                        color: '#8c8c8c',
                      }}>
                      Note: If you select Both, this option will be visible to
                      all attendees. If you select In-Person or Webcast, this
                      option will only be shown to attendees who match the
                      selected attendance type.
                    </div>
                  </div>
                </div>
              )}

              {showRegistration && (
                <div className='input-container'>
                  <div className='form-column'>
                    <Form.Item
                      name='requires_qualification_form'
                      valuePropName='checked'>
                      <Checkbox>Require qualification form (Tally)</Checkbox>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, cur) =>
                        prev.requires_qualification_form !==
                        cur.requires_qualification_form
                      }>
                      {({getFieldValue}) =>
                        getFieldValue('requires_qualification_form') ? (
                          <Form.Item
                            label='Tally form URL'
                            name='tally_form_url'
                            rules={[
                              {
                                required: true,
                                message: 'Tally form URL is required',
                              },
                              {
                                type: 'url',
                                message: 'Enter a valid URL (https://tally.so/r/...)',
                              },
                            ]}>
                            <Input
                              prefixCls='common-input-user'
                              className='genral-form'
                              placeholder='https://tally.so/r/your-form-id'
                            />
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                    <div
                      style={{
                        marginTop: -8,
                        marginBottom: 8,
                        fontSize: 12,
                        color: '#8c8c8c',
                      }}>
                      Add a hidden field named <strong>event_registration_id</strong> in
                      Tally. When you save this option, PBSS auto-attaches the webhook
                      via the Tally API (requires <code>TALLY_API_KEY</code>). You can
                      still set it manually to{' '}
                      <strong>/v1/tallyWebhooks/submission</strong> if needed.
                    </div>
                  </div>
                </div>
              )}

              {showSponsorPerks && (
                <div>
                  <div className='input-container'>
                    <div className='form-column'>
                      <Form.Item
                        label='Sponsorship Type'
                        name='sponsorship_type'
                        rules={[
                          {
                            required: true,
                            message: 'Sponsorship Type should not be empty',
                          },
                        ]}
                        valuePropName='value'>
                        <Select
                          prefixCls='common-select-register'
                          placeholder='Select Sponsorship Type'>
                          {Object.entries(SponsorshipType).map(
                            ([key, value]) => (
                              <Option key={key} value={value}>
                                {key}
                              </Option>
                            ),
                          )}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>

                  <div className='input-container'>
                    <div className='form-column'>
                      <Form.Item
                        label='Start Date'
                        name='start_date'
                        rules={[
                          {
                            required: true,
                            message: 'Start date should not be empty',
                          },
                        ]}
                        getValueFromEvent={(e) => e?.format('YYYY-MM-DD')}
                        getValueProps={(e) => ({value: e && dayjs(e)})}>
                        <DatePicker
                          disabledDate={(current) =>
                            disablePrevDatesHandler(current, true)
                          }
                          placeholder='Select event option start date'
                          prefixCls='common-input-user'
                          className='genral-form'
                        />
                      </Form.Item>
                      <Form.Item
                        label='Meeting Presentation'
                        name='workshop_attendees'
                        rules={[
                          {
                            required: true,
                            message: 'Meeting Presentation should not be empty',
                          },
                          {
                            validator: (_, value) =>
                              validateEventOptionNumberInput(
                                value,
                                'Meeting Presentation',
                                10,
                              ),
                          },
                        ]}>
                        <Input
                          prefixCls='common-input-user'
                          className='genral-form'
                          placeholder='Enter the number of Meeting Presentation'
                        />
                      </Form.Item>
                      <Form.Item
                        label='Lunch Limit'
                        name='free_lunch'
                        rules={[
                          {
                            required: true,
                            message: 'Lunch Limit should not be empty',
                          },
                          {
                            validator: (_, value) =>
                              validateEventOptionNumberInput(
                                value,
                                'Free lunch',
                              ),
                          },
                        ]}>
                        <Input
                          prefixCls='common-input-user'
                          className='genral-form'
                          placeholder='Enter number of free lunch'
                        />
                      </Form.Item>
                      <Form.Item
                        label='Dinner with Speaker'
                        name='dinner_with_speaker'
                        rules={[
                          {
                            required: true,
                            message: 'Dinner with speaker should not be empty',
                          },
                          {
                            validator: (_, value) =>
                              validateEventOptionNumberInput(
                                value,
                                'Dinner with speaker',
                                10,
                              ),
                          },
                        ]}>
                        <Input
                          prefixCls='common-input-user'
                          className='genral-form'
                          placeholder='Enter number of dinner with speaker'
                        />
                      </Form.Item>
                    </div>
                    <div className='form-column'>
                      <Form.Item
                        label='End Date'
                        name='end_date'
                        rules={[
                          {
                            required: true,
                            message: 'End date should not be empty',
                          },
                          ({getFieldValue}) => ({
                            validator(_, value) {
                              const startDate = getFieldValue('start_date');
                              const endDateValue = dayjs(value);
                              if (!startDate || !endDateValue) {
                                return Promise.resolve();
                              }
                              if (
                                endDateValue &&
                                endDateValue?.isAfter(startDate)
                              ) {
                                return Promise.resolve();
                              } else {
                                return Promise.reject(
                                  'End Date must be after Start Date',
                                );
                              }
                            },
                          }),
                        ]}
                        getValueFromEvent={(e) => e?.format('YYYY-MM-DD')}
                        getValueProps={(e) => ({value: e && dayjs(e)})}>
                        <DatePicker
                          disabledDate={(current) =>
                            disablePrevDatesHandler(current, true)
                          }
                          placeholder='Select event option end date'
                          prefixCls='common-input-user'
                          className='genral-form'
                        />
                      </Form.Item>
                      <Form.Item
                        label='Attendees Price'
                        name='attendees_price'
                        disabled
                        rules={[
                          {
                            validator: (_, value) =>
                              NumberWithoutDecimalValidation(
                                value,
                                'Attendees price',
                              ),
                          },
                        ]}>
                        <Input
                          prefixCls='common-input-user'
                          className='genral-form'
                          placeholder='Enter attendees price'
                        />
                      </Form.Item>
                      <Form.Item
                        label='Lunch Price'
                        name='lunch_price'
                        rules={[
                          {
                            required: false,
                            message: 'Lunch price should not be empty',
                          },
                          {
                            validator: (_, value) =>
                              NumberWithoutDecimalValidation(
                                value,
                                'Lunch price',
                              ),
                          },
                        ]}>
                        <Input
                          prefixCls='common-input-user'
                          className='genral-form'
                          placeholder='Enter lunch price'
                        />
                      </Form.Item>
                      <Form.Item
                        label='Dinner with Speaker Price'
                        name='dinner_with_speaker_price'
                        rules={[
                          {
                            required: false,
                            message:
                              'Dinner with speaker price should not be empty',
                          },
                          {
                            validator: (_, value) =>
                              NumberWithoutDecimalValidation(
                                value,
                                'Dinner with speaker price',
                              ),
                          },
                        ]}>
                        <Input
                          prefixCls='common-input-user'
                          className='genral-form'
                          placeholder='Enter dinner with speaker price'
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className='input-container'>
                    <div className='form-column'>
                      <Form.Item
                        label={
                          <div className='detailLabelAndTextMessageContainer'>
                            <span className='detailsLabel'>
                              Limit for vendors attending the vendor show for
                              free.{' '}
                            </span>
                            <span className='detailsTextMessage'>
                              <InfoCircleOutlined /> If you add vendor rep limit
                              as 0, it will be treated as unlimited.
                            </span>
                          </div>
                        }
                        name='vendor_rep_limit'
                        rules={[
                          {
                            required: true,
                            message: 'Vendor Rep Limit should not be empty',
                          },
                          {
                            validator: (_, value) =>
                              NumberWithoutDecimalValidation(
                                value,
                                'Vendor Rep Limit',
                              ),
                          },
                        ]}>
                        <Input
                          prefixCls='common-input-user'
                          className='genral-form'
                          placeholder='Limit for vendors attending the vendor show for free.'
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className='input-container'>
                    <div className='form-column'>
                      <Form.Item
                        label='Sponsorship Benefits'
                        name='benefits'
                        rules={[
                          {
                            required: true,
                            message: 'Sponsorship benefits should not be empty',
                          },
                        ]}>
                        <CommonEditor
                          value={editorInfoState}
                          onChange={setEditorInfoState}
                          text='sponsorship benefits'
                          placeholderValue='sponsorship benefits'
                        />
                      </Form.Item>
                    </div>
                  </div>
                  <div className='input-container'>
                    <div className='form-column'>
                      <Form.Item
                        label='Sponsor Contact Content'
                        name='sponsor_contact_content'
                        rules={[
                          {
                            required: false,
                            message:
                              'Sponsor contact content should not be empty',
                          },
                        ]}>
                        <CommonEditor
                          value={editorInfoState}
                          onChange={setEditorInfoState}
                          text='sponsor contact content'
                          placeholderValue='sponsor contact content'
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className='input-container'>
                    <div className='form-column'>
                      <Form.Item
                        label='Sponsorship Benefits Summary'
                        name='sponsor_perk_content'>
                        <CommonEditor
                          value={editorInfoState}
                          onChange={setEditorInfoState}
                          text='sponsorship benefits summary'
                          placeholderValue='sponsorship benefits summary'
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              )}

              <Form.Item>
                <div className='button-container'>
                  <Button
                    id='adminEventOptions-backButton-button'
                    className='common-submit-login common-form-submit common-form-cancel'
                    onClick={() => {
                      backToEventOptions();
                    }}>
                    Cancel
                  </Button>
                  <div className='form-flex-button'>
                    <Button
                      id='adminEventOptions-submitButton-button'
                      type='primary'
                      htmlType='submit'
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className='common-submit-login common-form-submit'>
                      {submitLabel}
                    </Button>
                    <Button
                      id='adminEventOptions-resetButton-button'
                      className='common-submit-login common-form-submit common-form-cancel'
                      htmlType='button'
                      disabled={isSubmitting}
                      onClick={onReset}>
                      Clear
                    </Button>
                  </div>
                </div>
              </Form.Item>
            </Form>
          </Col>
          <Col span={8}>
            <EventOptionDetails />
          </Col>
        </Row>
      </div>
    </div>
  );
};
export default EventOptionsFormComponent;
